<script>
import Layout from '@/views/layouts/main'
import PageHeader from '@/components/page-header'
import i18n from '@/i18n'
import { required } from 'vuelidate/lib/validators'
import { mapState } from 'vuex'
import { ROLE_MODULE } from '@/state/modules/role'
import { roleMethods } from '@/state/helpers'

/**
 * The role editor.
 *
 * ── Two ways to pick permissions, and both are needed ───────────────────
 * The checkbox grid is how you AUDIT a role: everything the system can do, in
 * module order, with the granted ones ticked — you can see what is missing,
 * which a list of chips cannot show. The multi-select above it is how you
 * CHANGE one: forty rights is too many to hunt through visually, and it is
 * searchable.
 *
 * They are two views of the same array, not two pieces of state. Keeping a
 * separate `selected` mirror is what made one_sales' forms show a blank
 * dropdown over a correctly-ticked grid.
 */
export default {
  page() {
    return { title: this.title }
  },
  components: { Layout, PageHeader },
  data() {
    return {
      form: { name: '', permissionCodes: [] },
      loading: false,
      submitting: false,
    }
  },
  validations: { form: { name: { required } } },
  computed: {
    ...mapState(ROLE_MODULE, { permissionGroups: (state) => state.permissions }),
    isEdit() {
      return !!this.$route.params.id
    },
    title() {
      return this.isEdit
        ? i18n.t('label.updateX', [i18n.t('module.role')])
        : i18n.t('label.createX', [i18n.t('module.role')])
    },
    breadcrumbItems() {
      return [
        { text: i18n.t('module.admin'), href: '/' },
        { text: i18n.t('module.role'), href: '/roles' },
        { text: this.title, active: true },
      ]
    },
    /**
     * Every permission code, flat.
     *
     * There used to be a searchable dropdown of these above the grid, which is
     * where the module-prefixed labels came from — "Read" appears five times
     * and a dropdown gives no other context. The grid below says the same thing
     * better: it is already grouped by module, already shows the whole list,
     * and ticking a box is one click where the dropdown was two. Two controls
     * bound to one array is also two places to look when a tick does not stick.
     */
    allCodes() {
      return this.permissionGroups.reduce(
        (all, group) => all.concat((group.permissions || []).map((p) => p.code)),
        []
      )
    },
  },
  async created() {
    this.loading = true

    try {
      // Before the detail, not alongside it: the grid cannot render a tick for
      // a code it has no row for.
      await this.lookupPermission()

      if (this.isEdit) {
        const result = await this.getRole({ id: this.$route.params.id })
        const d = result && result.data

        if (d) {
          this.form.name = d.name || ''
          this.form.permissionCodes = (d.permissions || []).map((p) => p.code)
        }
      }
    } finally {
      this.loading = false
    }
  },
  methods: {
    ...roleMethods,

    isChecked(code) {
      return this.form.permissionCodes.includes(code)
    },

    togglePermission(code, checked) {
      if (checked) {
        if (!this.form.permissionCodes.includes(code)) this.form.permissionCodes.push(code)
      } else {
        this.form.permissionCodes = this.form.permissionCodes.filter((c) => c !== code)
      }
    },

    moduleCodes(group) {
      return (group.permissions || []).map((p) => p.code)
    },

    isModuleAllChecked(group) {
      const codes = this.moduleCodes(group)
      return codes.length > 0 && codes.every((c) => this.form.permissionCodes.includes(c))
    },

    isModuleIndeterminate(group) {
      const codes = this.moduleCodes(group)
      const selected = codes.filter((c) => this.form.permissionCodes.includes(c)).length
      return selected > 0 && selected < codes.length
    },

    toggleModule(group, checked) {
      const codes = this.moduleCodes(group)

      if (checked) {
        this.form.permissionCodes = [...new Set([...this.form.permissionCodes, ...codes])]
      } else {
        this.form.permissionCodes = this.form.permissionCodes.filter((c) => !codes.includes(c))
      }
    },

    selectAll() {
      this.form.permissionCodes = [...this.allCodes]
    },

    unselectAll() {
      this.form.permissionCodes = []
    },

    async submit() {
      this.$v.form.$touch()
      if (this.$v.form.$invalid) return

      this.submitting = true

      try {
        const result = this.isEdit
          ? await this.updateRole({ id: this.$route.params.id, data: this.form })
          : await this.createRole({ data: this.form })

        if (result) this.$router.push({ name: 'Roles-listing' })
      } finally {
        this.submitting = false
      }
    },

    cancel() {
      this.$router.push({ name: 'Roles-listing' })
    },
  },
}
</script>

<template>
  <Layout>
    <PageHeader :title="title" :items="breadcrumbItems" />
    <div class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-body">
            <b-form @submit.prevent="submit">
              <b-row>
                <b-col cols="12">
                  <b-form-group :label="$t('label.name')" label-for="name">
                    <b-form-input
                      id="name"
                      v-model.trim="form.name"
                      :state="$v.form.name.$dirty ? !$v.form.name.$error : null"
                    />
                    <b-form-invalid-feedback>
                      {{ $t('message.requiredX', [$t('label.name')]) }}
                    </b-form-invalid-feedback>
                  </b-form-group>
                </b-col>
              </b-row>

              <div class="d-flex align-items-center mt-3 mb-3">
                <h5 class="mb-0">
                  {{ $t('label.permissions') }}
                  <span class="text-muted font-size-14">
                    ({{ form.permissionCodes.length }}/{{ allCodes.length }})
                  </span>
                </h5>
                <div class="ml-auto">
                  <b-button variant="outline-primary" size="sm" class="mr-2" @click="selectAll">
                    {{ $t('label.selectAll') }}
                  </b-button>
                  <b-button variant="outline-secondary" size="sm" @click="unselectAll">
                    {{ $t('label.unselectAll') }}
                  </b-button>
                </div>
              </div>

              <div v-if="loading" class="text-muted small">{{ $t('label.loading') }}</div>

              <div v-for="group in permissionGroups" :key="group.module" class="mb-3 p-3 border rounded">
                <b-form-checkbox
                  class="mb-2"
                  :checked="isModuleAllChecked(group)"
                  :indeterminate="isModuleIndeterminate(group)"
                  @change="(v) => toggleModule(group, v)"
                >
                  <span class="text-primary font-weight-bold">{{ group.moduleLabel }}</span>
                </b-form-checkbox>
                <b-row>
                  <b-col v-for="p in group.permissions" :key="p.code" cols="6" md="4" lg="3">
                    <b-form-checkbox
                      :checked="isChecked(p.code)"
                      @change="(v) => togglePermission(p.code, v)"
                    >
                      {{ p.name }}
                    </b-form-checkbox>
                  </b-col>
                </b-row>
              </div>

              <div class="text-right">
                <b-button variant="light" class="mr-2" @click="cancel">{{ $t('label.cancel') }}</b-button>
                <b-button type="submit" variant="primary" :disabled="submitting || loading">
                  {{ $t('label.submit') }}
                </b-button>
              </div>
            </b-form>
          </div>
        </div>
      </div>
    </div>
  </Layout>
</template>
